import React, { useState, useRef, useEffect } from 'react';
import { motion, useMotionValue, useSpring, AnimatePresence } from 'framer-motion';
import { Box, Typography } from '@mui/material';

const MagneticButton = ({ 
  children, 
  strength = 0.3,
  disabled = false,
  onClick,
  ...props 
}) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  
  const springX = useSpring(x, { stiffness: 300, damping: 30 });
  const springY = useSpring(y, { stiffness: 300, damping: 30 });

  const handleMouseMove = (e) => {
    if (!ref.current || disabled) return;
    
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const deltaX = (e.clientX - centerX) * strength;
    const deltaY = (e.clientY - centerY) * strength;
    
    x.set(deltaX);
    y.set(deltaY);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      style={{
        x: springX,
        y: springY,
        display: 'inline-block'
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={disabled ? undefined : onClick}
      {...props}
    >
      {children}
    </motion.div>
  );
};

const BounceClick = ({ 
  children, 
  bounceScale = 1.1,
  disabled = false,
  onClick,
  ...props 
}) => {
  const bounceVariants = {
    rest: { scale: 1 },
    hover: { 
      scale: 1.05,
      transition: { type: "spring", stiffness: 400, damping: 25 }
    },
    tap: {
      scale: bounceScale,
      transition: { 
        type: "spring", 
        stiffness: 600, 
        damping: 15,
        duration: 0.1
      }
    },
    disabled: {
      scale: 1,
      opacity: 0.6
    }
  };

  return (
    <motion.div
      variants={bounceVariants}
      initial="rest"
      whileHover={!disabled ? "hover" : "disabled"}
      whileTap={!disabled ? "tap" : "disabled"}
      animate={disabled ? "disabled" : "rest"}
      onClick={disabled ? undefined : onClick}
      style={{
        display: 'inline-block',
        cursor: disabled ? 'not-allowed' : 'pointer'
      }}
      {...props}
    >
      {children}
    </motion.div>
  );
};

const ProgressButton = ({ 
  children, 
  loading = false,
  progress = 0,
  onClick,
  color = '#0066cc',
  ...props 
}) => {
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (progress >= 100) {
      setIsComplete(true);
      setTimeout(() => setIsComplete(false), 2000);
    }
  }, [progress]);

  const buttonVariants = {
    idle: { 
      scale: 1,
      backgroundColor: color
    },
    loading: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: "easeInOut"
      }
    },
    complete: {
      scale: [1, 1.2, 1],
      backgroundColor: '#4caf50',
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20
      }
    }
  };

  const progressVariants = {
    initial: { width: '0%' },
    animate: { 
      width: `${progress}%`,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <motion.div
        variants={buttonVariants}
        animate={
          isComplete ? "complete" : 
          loading ? "loading" : "idle"
        }
        onClick={!loading && !isComplete ? onClick : undefined}
        style={{
          position: 'relative',
          overflow: 'hidden',
          cursor: loading || isComplete ? 'default' : 'pointer',
          borderRadius: 8
        }}
        {...props}
      >
        {children}
        
        {loading && (
          <motion.div
            variants={progressVariants}
            initial="initial"
            animate="animate"
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              height: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '0 0 8px 8px'
            }}
          />
        )}
      </motion.div>
      
      <AnimatePresence>
        {isComplete && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'white',
              fontSize: '1.2em'
            }}
          >
            ✓
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

const SwipeAction = ({ 
  children, 
  onSwipeLeft, 
  onSwipeRight,
  threshold = 50,
  ...props 
}) => {
  const [dragDirection, setDragDirection] = useState(null);

  const handleDragEnd = (event, info) => {
    const { offset, velocity } = info;
    
    if (Math.abs(offset.x) > threshold || Math.abs(velocity.x) > 500) {
      if (offset.x > 0 && onSwipeRight) {
        onSwipeRight();
      } else if (offset.x < 0 && onSwipeLeft) {
        onSwipeLeft();
      }
    }
    
    setDragDirection(null);
  };

  const handleDrag = (event, info) => {
    const direction = info.offset.x > 0 ? 'right' : 'left';
    setDragDirection(direction);
  };

  const dragConstraints = { left: -100, right: 100, top: 0, bottom: 0 };

  return (
    <motion.div
      drag="x"
      dragConstraints={dragConstraints}
      dragElastic={0.3}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      whileDrag={{
        scale: 1.05,
        rotate: dragDirection === 'right' ? 2 : dragDirection === 'left' ? -2 : 0,
        boxShadow: "0 10px 30px rgba(0,0,0,0.3)"
      }}
      style={{
        cursor: 'grab'
      }}
      whileTap={{ cursor: 'grabbing' }}
      {...props}
    >
      {children}
      
      {dragDirection && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'absolute',
            top: '50%',
            [dragDirection === 'right' ? 'left' : 'right']: dragDirection === 'right' ? -30 : -30,
            transform: 'translateY(-50%)',
            fontSize: '1.5em',
            color: dragDirection === 'right' ? '#4caf50' : '#f44336'
          }}
        >
          {dragDirection === 'right' ? '→' : '←'}
        </motion.div>
      )}
    </motion.div>
  );
};

const FloatingActionButton = ({ 
  children, 
  visible = true,
  onClick,
  color = '#0066cc',
  size = 56,
  ...props 
}) => {
  const fabVariants = {
    hidden: {
      scale: 0,
      rotate: -180,
      opacity: 0
    },
    visible: {
      scale: 1,
      rotate: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 30
      }
    },
    hover: {
      scale: 1.1,
      boxShadow: "0 8px 25px rgba(0,0,0,0.3)",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25
      }
    },
    tap: {
      scale: 0.9
    }
  };

  const pulseVariants = {
    pulse: {
      scale: [1, 1.2, 1],
      opacity: [0.7, 0.3, 0.7],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <Box sx={{ position: 'relative' }}>
          <motion.div
            variants={pulseVariants}
            animate="pulse"
            style={{
              position: 'absolute',
              top: -5,
              left: -5,
              right: -5,
              bottom: -5,
              backgroundColor: color,
              borderRadius: '50%',
              opacity: 0.3,
              zIndex: -1
            }}
          />
          
          <motion.button
            variants={fabVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            whileHover="hover"
            whileTap="tap"
            onClick={onClick}
            style={{
              width: size,
              height: size,
              borderRadius: '50%',
              border: 'none',
              backgroundColor: color,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              fontSize: '1.5em'
            }}
            {...props}
          >
            {children}
          </motion.button>
        </Box>
      )}
    </AnimatePresence>
  );
};

const ToggleSwitch = ({ 
  checked = false, 
  onChange,
  disabled = false,
  size = 'medium',
  color = '#0066cc',
  ...props 
}) => {
  const sizes = {
    small: { width: 40, height: 20, thumb: 16 },
    medium: { width: 50, height: 25, thumb: 21 },
    large: { width: 60, height: 30, thumb: 26 }
  };
  
  const currentSize = sizes[size];

  const switchVariants = {
    off: {
      backgroundColor: '#ccc',
      transition: { duration: 0.3 }
    },
    on: {
      backgroundColor: color,
      transition: { duration: 0.3 }
    },
    disabled: {
      backgroundColor: '#e0e0e0',
      opacity: 0.6
    }
  };

  const thumbVariants = {
    off: {
      x: 2,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 30
      }
    },
    on: {
      x: currentSize.width - currentSize.thumb - 2,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 30
      }
    },
    tap: {
      scale: 1.1
    }
  };

  return (
    <motion.div
      variants={switchVariants}
      animate={disabled ? "disabled" : checked ? "on" : "off"}
      onClick={disabled ? undefined : () => onChange(!checked)}
      style={{
        width: currentSize.width,
        height: currentSize.height,
        borderRadius: currentSize.height / 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        position: 'relative',
        display: 'flex',
        alignItems: 'center'
      }}
      {...props}
    >
      <motion.div
        variants={thumbVariants}
        animate={checked ? "on" : "off"}
        whileTap={!disabled ? "tap" : ""}
        style={{
          width: currentSize.thumb,
          height: currentSize.thumb,
          backgroundColor: 'white',
          borderRadius: '50%',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          position: 'absolute'
        }}
      />
    </motion.div>
  );
};

export {
  MagneticButton,
  BounceClick,
  ProgressButton,
  SwipeAction,
  FloatingActionButton,
  ToggleSwitch
};